import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { MessageCircle, ArrowLeft } from 'lucide-react';
import { SEOHelmet, SEO_CONFIG } from '@/components/SEOHelmet';
import Footer from '@/components/Footer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORIES = [
  { value: 'öneri', label: '💡 Öneri', icon: '💡' },
  { value: 'şikayet', label: '😔 Şikayet', icon: '😔' },
  { value: 'hata', label: '🐛 Hata Bildirimi', icon: '🐛' },
  { value: 'genel', label: '💬 Genel', icon: '💬' },
];

export default function Feedback() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    category: 'genel',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      toast.error('Lütfen tüm alanları doldurun');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/feedback`, formData);
      setSubmitted(true);
      toast.success('Geribildirminiz alındı. Teşekkür ederiz!');
    } catch (error) {
      toast.error('Bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEOHelmet 
        title={SEO_CONFIG.feedback.title}
        description={SEO_CONFIG.feedback.description}
      />
      <div className="min-h-screen bg-gradient-to-b from-purple-50 to-white flex flex-col">
        <div className="flex-1 flex items-center justify-center px-4 py-12">
          <Card className="w-full max-width: 600px; margin: 0 auto;">
            <CardHeader className="text-center">
              <div className="flex justify-center mb-4">
                <MessageCircle className="w-16 h-16 text-purple-600" />
              </div>
              <CardTitle className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                Geribildirim
              </CardTitle>
              <CardDescription className="text-base">
                Görüş, öneri ve şikayetleriniz bizim için değerlidir
              </CardDescription>
            </CardHeader>

            <CardContent>
              {!submitted ? (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Adınız *</Label>
                    <Input
                      id="name"
                      type="text"
                      placeholder="Adınız Soyadınız"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email">E-posta *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="ornek@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="category">Kategori *</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Kategori seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {CATEGORIES.map((cat) => (
                          <SelectItem key={cat.value} value={cat.value}>
                            {cat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Mesajınız *</Label>
                    <Textarea
                      id="message"
                      placeholder="Lütfen detaylı olarak yazın..."
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      rows={6}
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                    disabled={loading}
                  >
                    {loading ? 'Gönderiliyor...' : 'Gönder'}
                  </Button>
                </form>
              ) : (
                <div className="text-center space-y-4 py-8">
                  <div className="text-6xl">✅</div>
                  <h3 className="text-2xl font-bold text-purple-900">
                    Teşekkür Ederiz!
                  </h3>
                  <p className="text-gray-600">
                    Geribildirminiz alındı. En kısa sürede değerlendireceğiz.
                  </p>
                  <Button
                    onClick={() => navigate('/')}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Ana Sayfaya Dön
                  </Button>
                </div>
              )}

              {!submitted && (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate(-1)}
                  className="w-full mt-4"
                >
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Geri
                </Button>
              )}
            </CardContent>
          </Card>
        </div>
        <Footer />
      </div>
    </>
  );
}
